import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";

// 4-2-6 cycle → 12s per breath, 2 min total = 10 cycles.
const TOTAL_MS = 120000; // 2 minutes
const PREP_AUTO_MS = 7000; // gentle safety net if user doesn't tap

const PHASES = [
  { label: "Breathe in", ms: 4000, scale: 1.55, opacity: 0.9 },
  { label: "Hold", ms: 2000, scale: 1.55, opacity: 0.9 },
  { label: "Exhale", ms: 6000, scale: 1.0, opacity: 0.35 },
];

export default function Breathing() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  // prepare (tap to begin) → breathing (circle animation)
  const [phase, setPhase] = useState("prepare");

  const begin = () => {
    if (phase !== "prepare") return;
    setPhase("breathing");
  };

  // Auto-advance safety — never a hard gate. If user just sits with it,
  // breathing starts on its own after a soft pause.
  useEffect(() => {
    if (phase !== "prepare") return;
    const t = setTimeout(() => setPhase("breathing"), PREP_AUTO_MS);
    return () => clearTimeout(t);
  }, [phase]);

  // Cycle the 4-2-6 phases while in breathing.
  useEffect(() => {
    if (phase !== "breathing") return;
    let idx = 0;
    let timer;
    const tick = () => {
      setPhaseIdx(idx);
      timer = setTimeout(() => {
        idx = (idx + 1) % PHASES.length;
        tick();
      }, PHASES[idx].ms);
    };
    tick();
    return () => clearTimeout(timer);
  }, [phase]);

  // Elapsed timer + auto-navigate to closing.
  useEffect(() => {
    if (phase !== "breathing") return;
    const start = Date.now();
    const iv = setInterval(() => {
      const e = Date.now() - start;
      setElapsed(e);
      if (e >= TOTAL_MS) {
        clearInterval(iv);
        navigate(`/closing/${sessionId}`);
      }
    }, 250);
    return () => clearInterval(iv);
  }, [phase, navigate, sessionId]);

  const p = PHASES[phaseIdx];
  const progress = Math.min(1, elapsed / TOTAL_MS);

  return (
    <div
      className="flex flex-col min-h-[100dvh] items-center justify-between px-8 pt-10 pt-safe pb-12 pb-safe bg-still-bg"
      onClick={phase === "prepare" ? begin : undefined}
      role={phase === "prepare" ? "button" : undefined}
      data-testid={phase === "prepare" ? "prepare-screen" : "breathing-screen"}
    >
      <div className="w-full flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.3em] text-still-muted">
          {phase === "prepare" ? " " : "Breathing"}
        </span>
        <button
          data-testid="skip-breathing"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/closing/${sessionId}`);
          }}
          className="relative z-10 -m-3 p-3 text-still-muted text-sm hover:text-still-fg transition-colors"
        >
          Skip
        </button>
      </div>

      <div className="relative flex-1 w-full grid place-items-center">
        <AnimatePresence mode="wait">
          {phase === "prepare" ? (
            <motion.div
              key="prepare"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="text-center px-6 select-none"
            >
              <p
                data-testid="prepare-line"
                className="font-serif italic text-3xl text-still-fg/90 leading-relaxed"
              >
                Ready to take a moment to breathe?
              </p>
              <div className="mt-10 flex flex-col items-center gap-3">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-still-breathe/60 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-still-breathe/80" />
                </span>
                <span className="text-still-muted text-[11px] uppercase tracking-[0.3em]">
                  Tap when you&apos;re ready
                </span>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="breathing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full h-full grid place-items-center"
            >
              <div className="absolute inset-0 grid place-items-center pointer-events-none">
                <motion.div
                  data-testid="breathing-circle"
                  className="breathe-circle rounded-full bg-still-breathe/40 blur-3xl"
                  style={{ width: 260, height: 260 }}
                  animate={{ scale: p.scale, opacity: p.opacity }}
                  transition={{ duration: p.ms / 1000, ease: [0.42, 0, 0.58, 1] }}
                />
              </div>
              <motion.div
                className="relative w-56 h-56 rounded-full border border-still-breathe/30"
                animate={{ scale: p.scale, opacity: 0.55 + p.opacity * 0.5 }}
                transition={{ duration: p.ms / 1000, ease: [0.42, 0, 0.58, 1] }}
              />
              <div
                key={p.label}
                className="absolute font-serif italic text-2xl text-still-fg/90 animate-fade-up"
                data-testid="breathing-label"
              >
                {p.label}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="w-full">
        <div className="h-[1px] w-full bg-still-border overflow-hidden rounded-full">
          <div
            className="h-full bg-still-breathe/60"
            style={{
              width: `${phase === "breathing" ? progress * 100 : 0}%`,
              transition: "width 0.4s linear",
            }}
          />
        </div>
        <p className="text-still-muted text-xs mt-4 text-center tracking-wide">
          {phase === "prepare" ? " " : "In for 4… hold for 2… out for 6."}
        </p>
      </div>
    </div>
  );
}
